import { useRef, useState } from 'react';
import { Modal } from 'antd';
import i18next from 'i18next';
import BaseButton from 'components/atoms/baseButton';
import common from 'constants/common';
import { makeid } from 'utils/helpers/makeid';

import s from './styles.module.scss';

import type { ReactNode } from 'react';

interface IAlertModalProps {
  open: boolean;
  title: string | ReactNode;
  message?: string | ReactNode;
  type: btnType;
  handleOk: () => void;
  handleCancel: (res?: boolean) => void;
  successBtnLabel?: string;
  rejectBtnLabel?: string;
  showReject?: boolean;
  closable?: boolean;
  messageTitle?: string | ReactNode;
  customMessage?: string | ReactNode;
  zIndex?: number;
  confirming?: boolean;
}

type btnType = 'primary' | 'danger';

const AlertModal = ({
  open,
  title,
  type,
  message,
  successBtnLabel,
  rejectBtnLabel,
  handleOk,
  handleCancel,
  messageTitle,
  customMessage,
  zIndex = 1070,
  showReject = true,
  closable = true,
  confirming = false,
}: IAlertModalProps) => {
  const modalStyles = {
    body: {
      padding: '32px 24px 64px 24px',
    },
    footer: {
      borderTop: `1px solid ${common.commonColors.gray}`,
      padding: '24px',
    },
  };

  return (
    <Modal
      title={title}
      open={open}
      closable={closable}
      onCancel={() => handleCancel()}
      maskClosable={closable}
      footer={[
        showReject && (
          <BaseButton
            key="form-cancel-button"
            type="secondary"
            label={rejectBtnLabel ?? i18next.t('controls.cancel')}
            onClick={() => handleCancel(false)}
            dataCy="form-cancel-button"
          />
        ),
        <BaseButton
          key="form-delete-button"
          type={type}
          label={
            successBtnLabel ??
            (type === 'danger'
              ? i18next.t('controls.delete')
              : i18next.t('controls.ok'))
          }
          onClick={handleOk}
          loading={confirming}
          dataCy="form-delete-button"
        />,
      ]}
      zIndex={zIndex}
      styles={modalStyles}
    >
      <div>
        <div className={s['alert-message']}>
          {customMessage ?? (
            <>
              {messageTitle && (
                <p
                  className={s['alert-title']}
                  style={{ paddingBottom: message ? 10 : 0 }}
                >
                  {messageTitle}
                </p>
              )}
              <p>{message}</p>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export interface IAlertOptions {
  title: string | ReactNode;
  message?: string | ReactNode;
  type?: 'primary' | 'danger';
  successBtnLabel?: string;
  rejectBtnLabel?: string;
  showReject?: boolean;
  closable?: boolean;
  customMessage?: string | ReactNode;
  messageTitle?: string | ReactNode;
  zIndex?: number;
  onConfirm?: () => Promise<void>;
  handleOk?: () => void;
  handleCancel?: (res: boolean | undefined) => void;
  key?: string;
}

const useAlert = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [options, setOptions] = useState<IAlertOptions>({
    title: '',
    message: '',
    type: 'primary',
    key: makeid(10),
  });
  const popupControllerRef = useRef<{ resolve: (v: boolean) => void } | null>(
    null,
  );

  const showAlert = async (optionsProps: IAlertOptions): Promise<boolean> => {
    setOptions(optionsProps);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      popupControllerRef.current = { resolve };
    });
  };

  const handleOk = async () => {
    if (options.onConfirm) {
      setConfirming(true);
      try {
        await options.onConfirm();
      } finally {
        setConfirming(false);
      }
    }
    setOpen(false);
    popupControllerRef.current?.resolve(true);
  };

  const handleCancel = (res: boolean | undefined) => {
    popupControllerRef.current?.resolve(res ?? false);
    setOpen(false);
  };

  return {
    showAlert,
    Alert: (
      <AlertModal
        open={open}
        type={options.type ?? 'primary'}
        title={options.title}
        message={options?.message}
        handleOk={() => {
          handleOk();
          if (options.handleOk) options.handleOk();
        }}
        handleCancel={(res) => {
          handleCancel(res);
          if (options.handleCancel) options.handleCancel(res);
        }}
        successBtnLabel={options?.successBtnLabel}
        rejectBtnLabel={options?.rejectBtnLabel}
        messageTitle={options?.messageTitle}
        customMessage={options?.customMessage}
        closable={options?.closable}
        zIndex={options?.zIndex}
        showReject={options?.showReject}
        confirming={confirming}
        key={options?.key}
      />
    ),
  };
};

export default useAlert;
